import { z, ZodSchema } from 'zod';

/**
 * This function should be used in lieu of JSON.parse always when the expected JSON schema is known.
 *
 * Example usage:
 * const segmentSplit = zJsonParse(gptOutput, zGptSegmentSplit);
 */
export function zJsonParse<T>(jsonStr: string, schema: ZodSchema<T>): T {
  const jsonObj = stringToJSONSchema.parse(jsonStr);
  return schema.parse(jsonObj);
}

const zJsonLiteral = z.union([z.string(), z.number(), z.boolean(), z.null()]);
type JsonLiteral = z.infer<typeof zJsonLiteral>;
const zJson: z.ZodType<Json> = z.lazy(() => z.union([zJsonLiteral, z.array(zJson), z.record(zJson)]));
type Json = JsonLiteral | { [key: string]: Json } | Json[];

export const stringToJSONSchema = z.string().transform((str, ctx): z.infer<z.ZodType<Json, z.ZodTypeDef, Json>> => {
  try {
    return JSON.parse(str);
  } catch (e) {
    ctx.addIssue({ code: 'custom', message: 'Invalid JSON' });
    return z.NEVER;
  }
});

export function zMatchesSchema<T>(obj: unknown, schema: ZodSchema<T>): obj is T {
  const result = schema.safeParse(obj);
  return result.success;
}

/**
 * `true` when every value the type `T` allows also parses against the schema `S`.
 *
 * Used by {@link responseSchemaFor} to check a response schema against the response type it
 * guards. This is what keeps such a schema from becoming an outage of its own. A misspelled key,
 * a required assertion on a field the contract declares optional, or a value set narrower than
 * the type permits all become type errors instead of a runtime rejection of a legitimate
 * payload. The converse is deliberately not checked: a response schema is expected to assert
 * *less* than the type declares, covering only the fields the UI is load-bearing on.
 *
 * The check is only as honest as the declared type: where a response type is narrower than what
 * the server really sends (see PLT-6185), a schema has to be widened past the type by hand, and
 * this proves nothing about that field.
 */
export type SchemaAccepts<T, S extends z.ZodTypeAny> = [T] extends [z.infer<S>] ? true : false;

/**
 * A response schema carrying the response type it has been proven to accept.
 *
 * `fetchAndValidateOrThrow` takes one of these instead of a bare `z.ZodTypeAny`, so that the
 * schema and the type the body is cast to cannot be mismatched at the call site. That mistake is
 * worth ruling out in the compiler because it fails in the silent direction: response schemas are
 * non-strict objects, so a schema belonging to a *different* endpoint usually still accepts the
 * payload, and the check quietly becomes a no-op.
 *
 * The phantom member is a method, not a property, so that it is compared bivariantly: a call site
 * that narrows a generic response type — `GetAccessDetailsResponse<T>` against the guard built for
 * `GetAccessDetailsResponse` — still type-checks, while a schema for an unrelated response does
 * not. It only ever exists in the type system; {@link responseSchemaFor} casts it on and nothing
 * reads it.
 */
export type ResponseSchemaFor<T> = z.ZodTypeAny & {
  readonly __acceptsResponseType: { accepts(value: T): void };
};

/**
 * Tie a response schema to the response type it guards, checking at compile time that the schema
 * accepts every value that type allows (see {@link SchemaAccepts}). Both type arguments are
 * written out at the call site, since the schema's own type is what the check is against:
 *
 * ```ts
 * const zGetFooResponseSchema = z.object({ … });
 * export const zGetFooResponse = responseSchemaFor<GetFooResponse, typeof zGetFooResponseSchema>(
 *   zGetFooResponseSchema,
 * );
 * ```
 *
 * A schema that rejects something the response type permits fails to satisfy the parameter type,
 * whose one property name is the error message.
 */
export function responseSchemaFor<T, S extends z.ZodTypeAny>(
  schema: SchemaAccepts<T, S> extends true ? S : { __schemaMustAcceptEveryValueOfTheResponseType: never },
): ResponseSchemaFor<T> {
  return schema as unknown as ResponseSchemaFor<T>;
}
